import React from 'react';
import { Input } from '@/components/ui/input';
import { PriceInput } from './PriceInput';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Section, Field, AddBtn, DelBtn, CoverField, GalleryField, ModuleOrderEditor, WorkingHoursField, LocationEditor, PromotionsEditor, ItemImagesField, withImages, ContactInput } from './TemplateFields';
import { OnlineOrderingSettings, MenuCategoriesEditor, TableReservationEditor } from './MenuModules';
import { PLUGINS, orderedSections } from '../../data/businessCatalog';
import { itemImages } from '../../lib/assets';
import { ImagePlus, CreditCard, Phone, Mail, Globe, Facebook, Instagram } from 'lucide-react';

const WEEKDAYS = ['Pr', 'Ot', 'Tr', 'Ce', 'Pk', 'Se', 'Sv'];  // index 0=Mon .. 6=Sun
const UNITS = [{ v: 'hour', l: 'stundā' }, { v: 'group', l: 'grupai' }, { v: 'package', l: 'komplekts' }];

// Selectable clock values (06:00–23:30, 30-min steps) for the bookable-hours editor.
const TIME_OPTS = (() => { const o = []; for (let m = 6 * 60; m <= 23 * 60 + 30; m += 30) o.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`); return o; })();
const _toMin = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + m; };
// Build the list of bookable start-times between `start` and `end` (end exclusive).
const genSlots = (start, end, step) => { const out = []; if (!step || step <= 0) return out; let t = _toMin(start); const e = _toMin(end); while (t < e) { out.push(`${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`); t += step; } return out; };
const SLOT_SELECT = 'h-10 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2';
// Backend default when a page has no custom slots (mirrors DEFAULT_SLOTS in business_page.py).
const DEFAULT_SLOTS_FE = ['10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'];

export default function ReservationsEditor({ storeTab, rc, setRc, mc, setMc, upload, uploading, pluginOn, togglePlugin, availPlugins }) {
  const updateSpec = (i, patch) => { const a = [...(rc.specialists || [])]; a[i] = { ...a[i], ...patch }; setRc({ specialists: a }); };
  const toggleWorkDay = (i, di) => { const cur = (rc.specialists?.[i]?.work_days) || []; const next = cur.includes(di) ? cur.filter((x) => x !== di) : [...cur, di].sort((a, b) => a - b); updateSpec(i, { work_days: next }); };
  const addOffDate = (i, d) => { const cur = (rc.specialists?.[i]?.off_dates) || []; if (!cur.includes(d)) updateSpec(i, { off_dates: [...cur, d].sort() }); };
  const removeOffDate = (i, oi) => { const cur = [...((rc.specialists?.[i]?.off_dates) || [])]; cur.splice(oi, 1); updateSpec(i, { off_dates: cur }); };
  // Bookable time-slot editor state (authoritative for the client booking calendar).
  const [hoursFrom, setHoursFrom] = React.useState('09:00');
  const [hoursTo, setHoursTo] = React.useState('18:00');
  const [hoursStep, setHoursStep] = React.useState(30);
  const [addSlotVal, setAddSlotVal] = React.useState('09:00');
  const slots = (rc.time_slots && rc.time_slots.length) ? rc.time_slots : DEFAULT_SLOTS_FE;
  const regenSlots = () => setRc({ time_slots: genSlots(hoursFrom, hoursTo, hoursStep) });
  const removeSlot = (t) => setRc({ time_slots: slots.filter((x) => x !== t) });
  const addSlot = (t) => { if (!slots.includes(t)) setRc({ time_slots: [...slots, t].sort((a, b) => _toMin(a) - _toMin(b)) }); };
  return (
          <div className="space-y-8">
            {storeTab === 'identity' && (<>
            <Section title="Pamatinformācija">
              <Field label="Sauklis / apraksts">
                <Input value={rc.tagline || ''} onChange={(e) => setRc({ tagline: e.target.value })}
                  placeholder="Piem. VR piedzīvojumi visai ģimenei" className="bg-white/5 border-white/10 text-white" data-testid="resv-tagline" />
              </Field>
              <CoverField url={rc.cover_url} uploading={uploading} onUpload={(f) => upload(f, (u) => setRc({ cover_url: u }))} testid="resv-cover-upload" />
              {pluginOn('about') && (
                <Field label="Par mums">
                  <Textarea value={rc.about || ''} onChange={(e) => setRc({ about: e.target.value })}
                    placeholder="Pastāsti par savu uzņēmumu, pieredzi, komandu..." rows={4} className="bg-white/5 border-white/10 text-white" data-testid="resv-about" />
                </Field>
              )}
            </Section>
            {/* Special page: restaurant-style online ordering (menu cart + daily special) */}
            {pluginOn('menu') && setMc && <OnlineOrderingSettings mc={mc} setMc={setMc} showDailyText />}
            </>)}

            {storeTab === 'build' && (<>
            <Section title="Papildmoduļi">
              <p className="text-white/40 text-sm mb-4">Ieslēdz tikai tos moduļus, kurus vēlies rādīt savā lapā. Izslēgtie moduļi netiek rādīti apmeklētājiem.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="plugins-toggles">
                {availPlugins.map((pid) => {
                  const p = PLUGINS[pid];
                  if (!p) return null;
                  const Icon = p.icon;
                  return (
                    <div key={pid} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`plugin-toggle-${pid}`}>
                      <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-amber-400" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-semibold truncate">{p.label}</p>
                        <p className="text-white/40 text-xs truncate">{p.desc}</p>
                      </div>
                      <Switch checked={pluginOn(pid)} onCheckedChange={() => togglePlugin(pid)} data-testid={`plugin-switch-${pid}`} />
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="Online maksājumi">
              <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4 mb-3" data-testid="payments-info-card">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-400/15 flex items-center justify-center shrink-0 mt-0.5">
                    <CreditCard className="w-4.5 h-4.5 text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold">Maksājumi caur KoLV · 10% komisija</p>
                    <p className="text-white/50 text-xs mt-1 leading-relaxed">Uzliec pakalpojumam cenu (€0 = bezmaksas). Klients izvēlas kā maksāt no tevis iespējotajām opcijām. Online apmaksa iet caur KoLV (tu saņem 90% maciņā); klātienes/attālinātā apmaksa rada 10% komisijas parādu, ko nokārto vēlāk.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-2" data-testid="resv-allow-online-row">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold">Maksāt online (karte caur KoLV)</p>
                  <p className="text-white/40 text-xs">Klients apmaksā uzreiz ar karti. Tu saņem 90% maciņā.</p>
                </div>
                <Switch checked={rc.allow_online !== false} onCheckedChange={(v) => setRc({ allow_online: v })} data-testid="resv-allow-online-switch" />
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="resv-allow-onsite-row">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold">Maksāt klātienē / attālināti</p>
                  <p className="text-white/40 text-xs">Klients apmaksā ārpus KoLV. Vēlāk atzīmē to kā samaksātu — 10% kļūst par parādu.</p>
                </div>
                <Switch checked={!!rc.allow_onsite} onCheckedChange={(v) => setRc({ allow_onsite: v })} data-testid="resv-allow-onsite-switch" />
              </div>
            </Section>

            <ModuleOrderEditor template="reservations" order={orderedSections('reservations', rc.module_order, rc.kind || 'general')} onChange={(v) => setRc({ module_order: v })} />
            </>)}

            {storeTab === 'content' && (<>
            {/* Special page: Ēdienkarte + Galdiņu rezervācija (restaurant engine) */}
            {pluginOn('menu') && setMc && <MenuCategoriesEditor mc={mc} setMc={setMc} upload={upload} uploading={uploading} />}
            {pluginOn('tables') && setMc && <TableReservationEditor mc={mc} setMc={setMc} />}
            <Section title="Pakalpojumi & cenas" action={
              <AddBtn onClick={() => setRc({ services: [...rc.services, { id: `new-${Date.now()}`, name: '', price: 0, unit: 'hour', duration_min: 60 }] })} testid="add-service" />
            }>
              {rc.services.length === 0 && <p className="text-white/40 text-sm">Pievieno vismaz vienu pakalpojumu.</p>}
              <div className="space-y-3">
                {rc.services.map((s, i) => (
                  <div key={s.id || i} className="flex flex-wrap items-center gap-2" data-testid={`service-row-${i}`}>
                    <ItemImagesField images={itemImages(s)} uploading={uploading}
                      onAddFile={(f) => upload(f, (u) => { const a = [...rc.services]; a[i] = withImages(s, [...itemImages(s), u]); setRc({ services: a }); })}
                      onRemoveAt={(ri) => { const a = [...rc.services]; a[i] = withImages(s, itemImages(s).filter((_, k) => k !== ri)); setRc({ services: a }); }}
                      testid={`service-image-${i}`} />
                    <Input value={s.name} onChange={(e) => { const a = [...rc.services]; a[i] = { ...s, name: e.target.value }; setRc({ services: a }); }}
                      placeholder="Nosaukums" className="flex-1 min-w-[160px] bg-white/5 border-white/10 text-white" />
                    <PriceInput value={s.price} emptyValue={0} decimals={2} onChange={(v) => { const a = [...rc.services]; a[i] = { ...s, price: v }; setRc({ services: a }); }}
                      placeholder="€" className="w-20 shrink-0 bg-white/5 border-white/10 text-white" data-testid={`service-price-${i}`} />
                    <select value={s.unit} onChange={(e) => { const a = [...rc.services]; a[i] = { ...s, unit: e.target.value }; setRc({ services: a }); }}
                      className="w-28 shrink-0 h-10 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2">
                      {UNITS.map((u) => <option key={u.v} value={u.v} className="bg-[#0A0A0A]">{u.l}</option>)}
                    </select>
                    <div className="flex items-center gap-1 shrink-0" title="Procedūras ilgums minūtēs">
                      <Input type="number" min="0" step="5" value={s.duration_min ?? ''}
                        onChange={(e) => { const a = [...rc.services]; a[i] = { ...s, duration_min: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0) }; setRc({ services: a }); }}
                        placeholder="ilgums" className="w-20 bg-white/5 border-white/10 text-white" data-testid={`service-duration-${i}`} />
                      <span className="text-white/40 text-xs">min</span>
                    </div>
                    <DelBtn onClick={() => setRc({ services: rc.services.filter((_, j) => j !== i) })} />
                  </div>
                ))}
              </div>
            </Section>

            {/* Authoritative bookable time slots — these appear in the client's booking calendar. */}
            <Section title="Pieejamie laiki rezervācijām">
              <p className="text-white/45 text-sm mb-4">Šie laiki parādās klientam rezervācijas kalendārā. Iestati sākumu, beigas un soli, tad spied “Ģenerēt laikus”. Vari arī pievienot vai noņemt atsevišķus laikus. (Sadaļa “Darba laiki” ir tikai informatīvs teksts un neietekmē kalendāru.)</p>
              <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="slots-generator">
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-white/40 mb-1">No</label>
                  <select value={hoursFrom} onChange={(e) => setHoursFrom(e.target.value)} className={SLOT_SELECT} data-testid="slots-from">
                    {TIME_OPTS.map((t) => <option key={t} value={t} className="bg-[#0A0A0A]">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-white/40 mb-1">Līdz</label>
                  <select value={hoursTo} onChange={(e) => setHoursTo(e.target.value)} className={SLOT_SELECT} data-testid="slots-to">
                    {TIME_OPTS.map((t) => <option key={t} value={t} className="bg-[#0A0A0A]">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-white/40 mb-1">Solis</label>
                  <select value={hoursStep} onChange={(e) => setHoursStep(Number(e.target.value))} className={SLOT_SELECT} data-testid="slots-step">
                    <option value={15} className="bg-[#0A0A0A]">15 min</option>
                    <option value={30} className="bg-[#0A0A0A]">30 min</option>
                    <option value={60} className="bg-[#0A0A0A]">60 min</option>
                  </select>
                </div>
                <button type="button" onClick={regenSlots} className="h-10 px-4 rounded-lg bg-amber-500 text-[#0A0A0A] text-sm font-semibold hover:bg-amber-400 transition-colors" data-testid="slots-generate">Ģenerēt laikus</button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="slots-list">
                {slots.length === 0 && <p className="text-white/40 text-sm">Nav neviena laika — ģenerē vai pievieno zemāk.</p>}
                {slots.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 text-sm bg-white/10 rounded-full pl-3 pr-1.5 py-1 text-white/85" data-testid={`slot-chip-${t}`}>
                    {t}
                    <button type="button" onClick={() => removeSlot(t)} className="w-4 h-4 rounded-full hover:bg-white/20 text-white/60 hover:text-white flex items-center justify-center" data-testid={`slot-remove-${t}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <select value={addSlotVal} onChange={(e) => setAddSlotVal(e.target.value)} className={SLOT_SELECT} data-testid="slot-add-select">
                  {TIME_OPTS.map((t) => <option key={t} value={t} className="bg-[#0A0A0A]">{t}</option>)}
                </select>
                <button type="button" onClick={() => addSlot(addSlotVal)} className="h-10 px-4 rounded-lg border border-white/15 text-white text-sm font-semibold hover:border-amber-500/50 transition-colors" data-testid="slot-add-btn">Pievienot laiku</button>
              </div>
            </Section>

            {pluginOn('specialists') && (
            <Section title="Speciālisti / Meistari" action={
              <AddBtn onClick={() => setRc({ specialists: [...(rc.specialists || []), { id: `new-${Date.now()}`, name: '', role: '', photo_url: '' }] })} testid="add-specialist" />
            }>
              {(rc.specialists || []).length === 0 && <p className="text-white/40 text-sm">Pievieno meistarus/speciālistus ar foto (nav obligāti).</p>}
              <div className="space-y-3">
                {(rc.specialists || []).map((sp, i) => (
                  <div key={sp.id || i} className="bg-white/5 rounded-xl p-3 space-y-3" data-testid={`specialist-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <label className="w-14 h-14 rounded-full overflow-hidden bg-white/10 flex items-center justify-center cursor-pointer shrink-0 border border-white/10">
                        {sp.photo_url ? <img src={sp.photo_url} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-5 h-5 text-white/40" />}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], (u) => updateSpec(i, { photo_url: u }))} data-testid={`specialist-photo-${i}`} />
                      </label>
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input value={sp.name} onChange={(e) => updateSpec(i, { name: e.target.value })} placeholder="Vārds Uzvārds" className="bg-white/5 border-white/10 text-white" data-testid={`specialist-name-${i}`} />
                        <Input value={sp.role || ''} onChange={(e) => updateSpec(i, { role: e.target.value })} placeholder="Amats (piem. Friziere)" className="bg-white/5 border-white/10 text-white" data-testid={`specialist-role-${i}`} />
                      </div>
                      <DelBtn onClick={() => setRc({ specialists: (rc.specialists || []).filter((_, j) => j !== i) })} />
                    </div>
                    {/* Work schedule */}
                    <div className="sm:pl-[68px] space-y-2.5">
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1.5">Darba dienas</p>
                        <div className="flex flex-wrap gap-1.5">
                          {WEEKDAYS.map((wd, di) => {
                            const on = (sp.work_days || []).includes(di);
                            return (
                              <button key={di} type="button" onClick={() => toggleWorkDay(i, di)}
                                className={`w-9 h-9 rounded-lg text-xs font-semibold border transition-colors ${on ? 'bg-amber-500 text-[#0A0A0A] border-amber-500' : 'border-white/15 text-white/60 hover:border-amber-500/50'}`}
                                data-testid={`specialist-${i}-workday-${di}`}>{wd}</button>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-white/30 mt-1">Ja nav atzīmēta neviena diena, speciālists strādā katru dienu.</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1.5">Brīvdienas (atvaļinājums / svētki)</p>
                        <div className="flex flex-wrap gap-2 items-center">
                          {(sp.off_dates || []).map((d, oi) => (
                            <span key={oi} className="inline-flex items-center gap-1 text-xs bg-white/10 rounded-full pl-2.5 pr-1.5 py-1 text-white/80" data-testid={`specialist-${i}-offdate-${oi}`}>
                              {d}
                              <button type="button" onClick={() => removeOffDate(i, oi)} className="w-4 h-4 rounded-full hover:bg-white/20 text-white/60 hover:text-white flex items-center justify-center">×</button>
                            </span>
                          ))}
                          <input type="date" onChange={(e) => { if (e.target.value) { addOffDate(i, e.target.value); e.target.value = ''; } }}
                            className="text-xs bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-white [color-scheme:dark]" data-testid={`specialist-${i}-add-offdate`} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
            )}

            {pluginOn('gift_cards') && (
            <Section title="Dāvanu kartes" action={
              <AddBtn onClick={() => setRc({ gift_cards: [...rc.gift_cards, { name: 'Dāvanu karte', amount: 25 }] })} testid="add-gift" />
            }>
              <div className="space-y-3">
                {rc.gift_cards.map((g, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input value={g.name} onChange={(e) => { const a = [...rc.gift_cards]; a[i] = { ...g, name: e.target.value }; setRc({ gift_cards: a }); }}
                      className="col-span-8 bg-white/5 border-white/10 text-white" />
                    <PriceInput value={g.amount} emptyValue={0} decimals={2} onChange={(v) => { const a = [...rc.gift_cards]; a[i] = { ...g, amount: v }; setRc({ gift_cards: a }); }}
                      className="col-span-3 bg-white/5 border-white/10 text-white" data-testid={`giftcard-amount-${i}`} />
                    <DelBtn onClick={() => setRc({ gift_cards: rc.gift_cards.filter((_, j) => j !== i) })} />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 mt-3" data-testid="gift-partial-toggle">
                <div>
                  <p className="text-white text-sm font-semibold">Atļaut daļēju izmantošanu</p>
                  <p className="text-white/40 text-xs">Klients var izmantot karti pa daļām; tiek rādīts atlikums (nevis tikai “izmantota”).</p>
                </div>
                <Switch checked={!!rc.gift_card_partial_redeem} onCheckedChange={(v) => setRc({ gift_card_partial_redeem: v })} data-testid="gift-partial-switch" />
              </div>
            </Section>
            )}

            {pluginOn('gallery') && (
            <GalleryField gallery={rc.gallery} onAdd={(f) => upload(f, (u) => setRc({ gallery: [...rc.gallery, u] }))} onRemove={(i) => setRc({ gallery: rc.gallery.filter((_, j) => j !== i) })} />
            )}

            {pluginOn('promotions') && (
              <PromotionsEditor promotions={rc.promotions || []} onChange={(v) => setRc({ promotions: v })} />
            )}

            {pluginOn('hours') && (
              <WorkingHoursField value={rc.working_hours} onChange={(v) => setRc({ working_hours: v })} />
            )}

            {pluginOn('map') && (
              <LocationEditor address={rc.address} lat={rc.lat} lng={rc.lng} onPatch={(p) => setRc(p)} />
            )}

            {pluginOn('faq') && (
            <Section title="BUJ (biežāk uzdotie jautājumi)" action={
              <AddBtn onClick={() => setRc({ faq: [...(rc.faq || []), { q: '', a: '' }] })} testid="add-faq" />
            }>
              {(rc.faq || []).length === 0 && <p className="text-white/40 text-sm">Pievieno jautājumus un atbildes.</p>}
              <div className="space-y-3">
                {(rc.faq || []).map((f, i) => (
                  <div key={i} className="bg-white/5 rounded-xl p-3 space-y-2" data-testid={`faq-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={f.q} onChange={(e) => { const a = [...(rc.faq || [])]; a[i] = { ...f, q: e.target.value }; setRc({ faq: a }); }}
                        placeholder="Jautājums" className="bg-white/5 border-white/10 text-white font-medium" data-testid={`faq-q-${i}`} />
                      <DelBtn onClick={() => setRc({ faq: (rc.faq || []).filter((_, j) => j !== i) })} />
                    </div>
                    <Textarea value={f.a || ''} onChange={(e) => { const a = [...(rc.faq || [])]; a[i] = { ...f, a: e.target.value }; setRc({ faq: a }); }}
                      placeholder="Atbilde" rows={2} className="bg-white/5 border-white/10 text-white" data-testid={`faq-a-${i}`} />
                  </div>
                ))}
              </div>
            </Section>
            )}

            {pluginOn('reviews') && (
            <Section title="Atsauksmes" action={
              <AddBtn onClick={() => setRc({ reviews: [...(rc.reviews || []), { author: '', rating: 5, text: '' }] })} testid="add-review" />
            }>
              {(rc.reviews || []).length === 0 && <p className="text-white/40 text-sm">Pievieno klientu atsauksmes.</p>}
              <div className="space-y-3">
                {(rc.reviews || []).map((r, i) => (
                  <div key={i} className="bg-white/5 rounded-xl p-3 space-y-2" data-testid={`review-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={r.author} onChange={(e) => { const a = [...(rc.reviews || [])]; a[i] = { ...r, author: e.target.value }; setRc({ reviews: a }); }}
                        placeholder="Klienta vārds" className="bg-white/5 border-white/10 text-white font-medium" data-testid={`review-author-${i}`} />
                      <select value={r.rating || 5} onChange={(e) => { const a = [...(rc.reviews || [])]; a[i] = { ...r, rating: parseInt(e.target.value, 10) }; setRc({ reviews: a }); }}
                        className="h-10 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2 shrink-0" data-testid={`review-rating-${i}`}>
                        {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n} className="bg-[#0A0A0A]">{n} ★</option>)}
                      </select>
                      <DelBtn onClick={() => setRc({ reviews: (rc.reviews || []).filter((_, j) => j !== i) })} />
                    </div>
                    <Textarea value={r.text || ''} onChange={(e) => { const a = [...(rc.reviews || [])]; a[i] = { ...r, text: e.target.value }; setRc({ reviews: a }); }}
                      placeholder="Atsauksmes teksts" rows={2} className="bg-white/5 border-white/10 text-white" data-testid={`review-text-${i}`} />
                  </div>
                ))}
              </div>
            </Section>
            )}

            {pluginOn('contacts') && (
            <Section title="Kontakti">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ContactInput icon={Phone} value={rc.contacts?.phone} onChange={(v) => setRc({ contacts: { ...rc.contacts, phone: v } })} placeholder="+371 20000000" testid="resv-phone" />
                <ContactInput icon={Mail} value={rc.contacts?.email} onChange={(v) => setRc({ contacts: { ...rc.contacts, email: v } })} placeholder="info@bizness.lv" testid="resv-email" />
                <ContactInput icon={Globe} value={rc.contacts?.website} onChange={(v) => setRc({ contacts: { ...rc.contacts, website: v } })} placeholder="www.bizness.lv" testid="resv-website" />
                <ContactInput icon={Facebook} value={rc.contacts?.facebook} onChange={(v) => setRc({ contacts: { ...rc.contacts, facebook: v } })} placeholder="facebook.com/..." testid="resv-facebook" />
                <ContactInput icon={Instagram} value={rc.contacts?.instagram} onChange={(v) => setRc({ contacts: { ...rc.contacts, instagram: v } })} placeholder="instagram.com/..." testid="resv-instagram" />
              </div>
            </Section>
            )}

            <Section title="Kontakta piezīme">
              <Textarea value={rc.contact_note || ''} onChange={(e) => setRc({ contact_note: e.target.value })}
                placeholder="Piem. Zvani iepriekš, lai apstiprinātu laiku." className="bg-white/5 border-white/10 text-white" data-testid="resv-note" />
            </Section>
            </>)}
          </div>
  );
}
